<template>
  <div :class="dialog_class">
    <v-btn
      v-if="show_button"
      x-small
      outlined
      color="primary"
      @click="openDialog"
    >
      <v-icon x-small left>mdi-eye</v-icon>
      {{ button_text }}
    </v-btn>
    <v-list-item v-else-if="show_list_item" @click="openDialog">
      <v-list-item-title>{{ button_text }}</v-list-item-title>
    </v-list-item>
    <span v-else-if="show_empty_text" class="text-caption grey--text text--lighten-1">{{ empty_text }}</span>
    <v-list-item v-else-if="show_empty_list_item" disabled>
      <v-list-item-title class="text-caption grey--text text--lighten-1">{{ empty_text }}</v-list-item-title>
    </v-list-item>

    <v-dialog v-model="details_dialog" :max-width="max_width">
      <v-card>
        <v-card-title class="headline d-flex align-center">
          {{ title }}
          <v-spacer></v-spacer>
          <v-btn-toggle v-model="view_mode" mandatory dense class="mr-2">
            <v-btn small value="formatted">
              <v-icon small left>mdi-file-tree</v-icon>
              Formatted
            </v-btn>
            <v-btn small value="raw">
              <v-icon small left>mdi-code-json</v-icon>
              Raw JSON
            </v-btn>
          </v-btn-toggle>
        </v-card-title>


        <v-card-text class="details-content">
          <p class="details-subtitle">
            The below are all of the technical details captured based on the user's selections when originally generating this import.
            Typically populated when pulling data from CRMs or external data sources.
          </p>

          <!-- Formatted Tree View -->
          <template v-if="view_mode === 'formatted'">
            <div class="d-flex justify-end mb-2">
              <v-btn x-small text color="primary" @click="expandAll">
                <v-icon x-small left>mdi-expand-all</v-icon>
                Expand All
              </v-btn>
              <v-btn x-small text color="primary" @click="collapseAll">
                <v-icon x-small left>mdi-collapse-all</v-icon>
                Collapse All
              </v-btn>
            </div>
            <v-card outlined class="tree-container">
              <import-details-tree
                ref="details_tree"
                :value="details"
                :open_depth="open_depth"
              />
            </v-card>
          </template>

          <!-- Raw JSON View -->
          <template v-else>
            <div class="d-flex justify-end mb-2">
              <v-btn x-small text color="primary" @click="copyRawJson">
                <v-icon x-small left>{{ copy_icon }}</v-icon>
                {{ copy_text }}
              </v-btn>
            </div>
            <v-card outlined class="raw-json-container">
              <pre class="raw-json-content">{{ formatted_json }}</pre>
            </v-card>
          </template>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="details_dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ImportDetailsTree from '@/components/imports/import-details-tree.vue';

export default {
  name: 'import-details-dialog',
  components: { ImportDetailsTree },
  props: {
    details: { type: [Object, Array, String], default: null },
    title: { type: String, default: 'Import Source Technical Details' },
    button_text: { type: String, default: 'View' },
    empty_text: { type: String, default: '-' },
    max_width: { type: Number, default: 760 },
    open_depth: { type: Number, default: 2 },
    activator_variant: { type: String, default: 'button' },
    hide_when_empty: { type: Boolean, default: false }
  },
  computed: {
    has_details() {
      return this.hasDetailContent(this.details);
    },
    show_button() {
      return this.activator_variant === 'button' && this.has_details;
    },
    show_list_item() {
      return this.activator_variant === 'list-item' && this.has_details;
    },
    show_empty_text() {
      return this.activator_variant === 'button' && !this.has_details && !this.hide_when_empty;
    },
    show_empty_list_item() {
      return this.activator_variant === 'list-item' && !this.has_details && !this.hide_when_empty;
    },
    dialog_class() {
      return {
        'import-details-dialog': true,
        'import-details-dialog--list': this.activator_variant === 'list-item'
      };
    },
    formatted_json() {
      if (!this.details) return '';
      try {
        const parsed = typeof this.details === 'string' ? JSON.parse(this.details) : this.details;
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return String(this.details);
      }
    },
    copy_icon() {
      return this.just_copied ? 'mdi-check' : 'mdi-content-copy';
    },
    copy_text() {
      return this.just_copied ? 'Copied!' : 'Copy to Clipboard';
    }
  },
  methods: {
    openDialog() {
      if (!this.has_details) {
        return;
      }
      this.details_dialog = true;
    },
    hasDetailContent(value) {
      if (value === null || value === undefined || value === '') {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === 'object') {
        return Object.keys(value).length > 0;
      }
      return true;
    },
    copyRawJson() {
      navigator.clipboard.writeText(this.formatted_json).then(() => {
        this.just_copied = true;
        setTimeout(() => {
          this.just_copied = false;
        }, 2000);
      }).catch(() => {
        this.$rc.snackbar('Failed to copy to clipboard', 'error');
      });
    },
    expandAll() {
      if (this.$refs.details_tree) {
        this.$refs.details_tree.expandAll();
      }
    },
    collapseAll() {
      if (this.$refs.details_tree) {
        this.$refs.details_tree.collapseAll();
      }
    }
  },
  watch: {
    details_dialog(is_open) {
      if (!is_open) {
        this.$emit('dialog-closed');
        this.view_mode = 'formatted';
        this.just_copied = false;
      }
    }
  },
  data() {
    return {
      details_dialog: false,
      view_mode: 'formatted',
      just_copied: false
    };
  }
};
</script>

<style scoped>
.import-details-dialog {
  display: inline-flex;
  align-items: center;
}

.import-details-dialog--list {
  display: block;
}

.details-subtitle {
  color: rgba(0, 0, 0, 0.6);
  margin: 0 0 16px 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.details-content {
  padding-top: 16px;
}

.tree-container {
  max-height: 400px;
  overflow-y: auto;
  background-color: #fafafa;
}

.raw-json-container {
  max-height: 400px;
  overflow: auto;
  background-color: #f5f5f5;
}

.raw-json-content {
  margin: 0;
  padding: 16px;
  font-family: 'Roboto Mono', 'Consolas', 'Monaco', monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: #333;
}
</style>
